import { Button, ButtonIcon, FontAwesomeIcon, Modal, ModalProps, Stack } from "@skailer-com/uikit";
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

interface PhotoViewerModalProps extends ModalProps {
  photos: string[];
  title?: string;
}

interface Position {
  x: number;
  y: number;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

export function PhotoViewerModal({ photos, title, ...props }: PhotoViewerModalProps) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const zoomRef = useRef(MIN_ZOOM);
  const viewportRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);

  const hasSeveralPhotos = photos.length > 1;
  const lastIndex = Math.max(photos.length - 1, 0);
  const photoIndex = Math.min(currentIndex, lastIndex);
  const currentPhoto = photos[photoIndex];

  const resetTransform = useCallback(() => {
    zoomRef.current = MIN_ZOOM;
    setZoom(MIN_ZOOM);
    setPosition({ x: 0, y: 0 });
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  const clampPosition = useCallback((nextPosition: Position, nextZoom: number): Position => {
    const viewport = viewportRef.current;
    const image = imageRef.current;

    if (!viewport || !image || nextZoom === MIN_ZOOM) return { x: 0, y: 0 };

    const maxX = Math.max(0, (image.clientWidth * nextZoom - viewport.clientWidth) / 2);
    const maxY = Math.max(0, (image.clientHeight * nextZoom - viewport.clientHeight) / 2);

    return {
      x: Math.min(maxX, Math.max(-maxX, nextPosition.x)),
      y: Math.min(maxY, Math.max(-maxY, nextPosition.y)),
    };
  }, []);

  const changeZoom = useCallback(
    (nextZoomValue: number, focalPoint?: Position) => {
      const currentZoom = zoomRef.current;
      const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextZoomValue));

      setPosition((currentPosition) => {
        if (nextZoom === MIN_ZOOM) return { x: 0, y: 0 };

        if (!focalPoint) return clampPosition(currentPosition, nextZoom);

        const zoomRatio = nextZoom / currentZoom;
        const nextPosition = {
          x: focalPoint.x - (focalPoint.x - currentPosition.x) * zoomRatio,
          y: focalPoint.y - (focalPoint.y - currentPosition.y) * zoomRatio,
        };

        return clampPosition(nextPosition, nextZoom);
      });

      zoomRef.current = nextZoom;
      setZoom(nextZoom);
    },
    [clampPosition]
  );

  const showPrevious = useCallback(() => {
    setCurrentIndex((index) => (Math.min(index, lastIndex) - 1 + photos.length) % photos.length);
    resetTransform();
  }, [lastIndex, photos.length, resetTransform]);

  const showNext = useCallback(() => {
    setCurrentIndex((index) => (Math.min(index, lastIndex) + 1) % photos.length);
    resetTransform();
  }, [lastIndex, photos.length, resetTransform]);

  useEffect(() => {
    if (!hasSeveralPhotos) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") showPrevious();
      if (event.key === "ArrowRight") showNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasSeveralPhotos, showNext, showPrevious]);

  useEffect(() => {
    const viewportElement = viewportRef.current;
    if (!viewportElement) return;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      const viewport = viewportElement.getBoundingClientRect();
      const focalPoint = {
        x: event.clientX - viewport.left - viewport.width / 2,
        y: event.clientY - viewport.top - viewport.height / 2,
      };
      const delta =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE ? event.deltaY * 16 : event.deltaY;
      const nextZoom = zoomRef.current * Math.exp(-delta * 0.002);

      changeZoom(nextZoom, focalPoint);
    };

    viewportElement.addEventListener("wheel", handleWheel, { passive: false });
    return () => viewportElement.removeEventListener("wheel", handleWheel);
  }, [changeZoom]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (
      zoom === MIN_ZOOM ||
      event.button !== 0 ||
      (event.target as HTMLElement).closest("button")
    ) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    setIsDragging(true);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - drag.x;
    const deltaY = event.clientY - drag.y;
    dragRef.current = { ...drag, x: event.clientX, y: event.clientY };
    setPosition((currentPosition) =>
      clampPosition({ x: currentPosition.x + deltaX, y: currentPosition.y + deltaY }, zoom)
    );
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setIsDragging(false);
  };

  return (
    <Modal
      {...props}
      layout={"page"}
      size={"full"}
      withCloser
      disableAutofocus
      closeByEscEnabled
      title={title}
    >
      <Stack className={"pt-6 h-full"}>
        <div
          ref={viewportRef}
          className={
            "relative flex min-h-0 grow touch-none select-none items-center justify-center overflow-hidden rounded-sm bg-dark/5"
          }
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDoubleClick={() => changeZoom(zoom === MIN_ZOOM ? 2 : MIN_ZOOM)}
          style={{ cursor: zoom > MIN_ZOOM ? (isDragging ? "grabbing" : "grab") : "zoom-in" }}
        >
          {currentPhoto ? (
            <img
              ref={imageRef}
              src={currentPhoto}
              className={"max-h-full max-w-full object-contain will-change-transform absolute"}
              draggable={false}
              style={{
                transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${zoom})`,
              }}
            />
          ) : (
            <div className={"text-secondary"}>{t("common:nothing_found")}</div>
          )}

          {photos.length > 1 && (
            <Stack
              className={"absolute left-4 top-4 rounded-xs bg-light/70 hover:bg-light/90 px-2 py-2"}
            >
              {photos.length ? photoIndex + 1 : 0} / {photos.length}
            </Stack>
          )}

          <Stack
            className={
              "absolute left-1/2 -translate-x-1/2 bottom-4 bg-light/70 p-2 rounded-xs hover:bg-light/90"
            }
            gap={"xs"}
            direction={"horizontal"}
            onPointerDown={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <ButtonIcon
              variant={"secondary"}
              title={t("common:zoom_out")}
              disabled={zoom <= MIN_ZOOM}
              onClick={() => changeZoom(zoom - ZOOM_STEP)}
              icon={<FontAwesomeIcon icon={"fa-light fa-minus"} />}
            />
            <Button variant={"secondary"} onClick={() => changeZoom(1)}>
              {Math.round(zoom * 100)}%
            </Button>
            <ButtonIcon
              variant={"secondary"}
              title={t("common:zoom_in")}
              disabled={zoom >= MAX_ZOOM}
              onClick={() => changeZoom(zoom + ZOOM_STEP)}
              icon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
            />
          </Stack>

          {hasSeveralPhotos && (
            <>
              <ButtonIcon
                variant={"secondary"}
                className={"absolute left-4 top-1/2 -translate-y-1/2"}
                title={t("common:previous")}
                onClick={showPrevious}
                size={"lg"}
                icon={<FontAwesomeIcon icon={"fa-light fa-chevron-left"} />}
              />
              <ButtonIcon
                variant={"secondary"}
                className={"absolute right-4 top-1/2 -translate-y-1/2"}
                title={t("common:next")}
                size={"lg"}
                onClick={showNext}
                icon={<FontAwesomeIcon icon={"fa-light fa-chevron-right"} />}
              />
            </>
          )}
        </div>
      </Stack>
    </Modal>
  );
}
